import React, {useContext, useEffect, useState} from "react";
import {ScreenWidthContext} from "../../../../Contexts/ScreenWidthContext";
import {useTranslation} from "react-i18next";
import {GET, POST, PUT} from "../../../../Hooks/RequestV2";
import {getCoreRowModel, getPaginationRowModel, useReactTable} from "@tanstack/react-table";
import ResponsiveTable from "../../../../Components/ResponsiveTable";
import {SimpleModal, useSimpleModalState} from "../../../../Components/SimpleModal";
import {DefaultValues, FieldValues, SubmitHandler, useForm} from "react-hook-form";

interface ProductLimit {
    id: number,
    name: string,
    productId: number[],
    products: ProductDataInterface[],
}

export default function ProductLimitList(): React.ReactElement {
    const [productsLimits, setProductsLimits] = useState<ProductLimit[]>([]);
    const screenWidth   = useContext(ScreenWidthContext);
    const {t}           = useTranslation();
    const {visibility, toggleVisibleModal, setVisibility} = useSimpleModalState();
    const [limitId, setLimitId] = useState<number | null>(null);
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        getList();
    }, [refresh]);

    function getList(): void {
        GET('/user/product-limit/show')
            .then((response) => {
                if (response.status === 200){
                    return response.json();
                }
            })
            .then((json) => {
                setProductsLimits(json);
            });
    }

    const columns = [
        {
            header: t('user-page.merchant-limit.name'),
            accessorKey: 'name'
        },
        {
            header: t('user-page.merchant-limit.options'),
            accessorKey: 'id',
            cell: ({getValue}: {getValue: any}) => {
                return (
                    <button className={'btn'} onClick={() => {
                        setLimitId(getValue() as number);
                        setVisibility(true as any);
                    }} >
                        <i className={'fas fa-edit'} /> {t('user-page.merchant-limit.edit')}
                    </button>
                );
            }
        },
    ];

    const table = useReactTable({
        data: productsLimits,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <>
            <ModalForm visibility={visibility} toggleVisibleModal={toggleVisibleModal} limitId={limitId} setRefresh={setRefresh} />

            <div className={'buttons-list'} >
                <button className={'btn'} onClick={() => {
                    setLimitId(null);
                    setVisibility(true as any);
                }} ><i className={'fas fa-plus'} /> {t('user-page.merchant-limit.add-new')}</button>
            </div>

            {productsLimits.length === 0 ?
                <div className={'empty'} >{t('user-page.merchant-limit.empty-list')}</div> :
                <ResponsiveTable maxColumns={screenWidth < 500 ? 2 : 3} table={table} additionalBottom={null} />}
        </>
    );
}

interface ModalFormInterface {
    visibility: boolean | null,
    toggleVisibleModal: () => void,
    limitId: number | null,
    setRefresh: React.Dispatch<React.SetStateAction<boolean>>,
}

interface ProductDataInterface {
    id: number,
    name: string,
    indexHurt: number,
    ean: string,
}

interface FormInterface {
    name: string,
    product_id: number[],
}

const defaultForm: DefaultValues<FormInterface> = {
    name: '',
    product_id: [],
};

const PRODUCTS_IDS_PROPERTY = 'product_id';

function ModalForm({visibility, toggleVisibleModal, limitId, setRefresh}: ModalFormInterface): React.ReactElement {
    const form = useForm({defaultValues: defaultForm});
    const searchForm = useForm();
    const {t} = useTranslation();

    const [productsAdded, setProductsAdded] = useState<ProductDataInterface[]>([]);
    const [productsFind, setProductsFind] = useState<ProductDataInterface[]>([]);
    const [startSearch, setStartSearch] = useState(false);
    const [searchThrobber, setSearchThrobber] = useState(false);

    useEffect(() => {
        if (limitId){
            GET('/user/product-limit/show/'+(limitId ?? 0))
                .then((response) => {
                    if (response.ok){
                        return response.json();
                    }
                })
                .then((json) => {
                    form.setValue('name', json.name);
                    form.setValue(PRODUCTS_IDS_PROPERTY, json.productId);
                    setProductsAdded(json.products);
                });
        }
    }, [limitId]);

    function send(): void {
        let request;

        if (limitId === null){
            request = POST('/user/product-limit/create', form.getValues());
        } else {
            request = PUT('/user/product-limit/edit/'+(limitId ?? 0), form.getValues());
        }

        request.then((response) => {
            if (response.ok){
                toggleVisibleModal();
                setRefresh((prev) => !prev);
            }
        });

    }

    function add(product: ProductDataInterface): void {
        const productsIds: number[] = form.watch(PRODUCTS_IDS_PROPERTY as any);

        if (productsIds?.includes(product.id)) return;

        setProductsAdded([...productsAdded, product]);
        form.setValue(PRODUCTS_IDS_PROPERTY as any, [...(productsIds ?? []), product.id] as any, {
            shouldDirty: true,
            shouldValidate: true,
        });

        setProductsFind(productsFind.filter((item: ProductDataInterface) => item.id !== product.id));
    }

    function remove(product: ProductDataInterface): void {
        setProductsAdded(productsAdded.filter((item: ProductDataInterface) => item.id !== product.id));

        const productsIds: number[] = form.watch(PRODUCTS_IDS_PROPERTY as any);

        form.setValue(PRODUCTS_IDS_PROPERTY, productsIds?.filter((id: number) => id !== product.id) as any, {});
    }

    const searchProduct: SubmitHandler<FieldValues> = (data) => {
        setSearchThrobber(true)

        const request = {
            "search": data.search,
            "s": true,
            "per-page": 50,
            "sort": 0,
            "menuitem": "s"
        }

        POST('/user/product-limit/search/product', request)
            .then((response) => {
                if (response.status === 200){
                    return response.json();
                }
            })
            .then((json) => {
                let productDTO: ProductDataInterface[] = [];

                json.productsList.products.map((product: any) => {
                    productDTO.push({
                        id: product.id,
                        name: product.translation.name,
                        indexHurt: product.hurtId,
                        ean: product.default_ean,
                    });
                });

                setStartSearch(true);
                setProductsFind(productDTO);
                setSearchThrobber(false);
            });
    }

    return (
        <SimpleModal visibility={visibility} wait={true} hidden={toggleVisibleModal} className={'product-thumb-modal'} >
            <form onSubmit={form.handleSubmit(send)} className={'limit-form'} >
                <h1>{t('user-page.merchant-limit.product-limit')}</h1>
                <div className={'field'} >
                    <label>{t('user-page.merchant-limit.name')}</label>
                    <input {...form.register('name')} />
                </div>

                <div className={'form-products-list'} >
                    {productsAdded.length === 0 ?  <div className={'empty'} >{t('user-page.merchant-limit.empty-product-list')}</div> :

                        <table>
                            {productsAdded.map((product: ProductDataInterface, index: number) => {
                                return (
                                    <tr key={index} className={'product'} >
                                        <td><b>{product.name}</b></td>
                                        <td>{product.indexHurt}</td>
                                        <td>{product.ean}</td>
                                        <td><button className={'btn red'} type={'button'} onClick={() => remove(product)} >{t('user-page.merchant-limit.remove')}</button></td>
                                    </tr>
                                );
                            })}
                        </table>

                    }
                </div>

                <div className={'buttons'} >
                    <button className={'btn'} >{t('user-page.merchant-limit.save')}</button>
                </div>
            </form>

            <form onSubmit={searchForm.handleSubmit(searchProduct)} className={'search-product'} >
                <h1>{t('user-page.merchant-limit.search-products')}</h1>
                <div className={'wrap'} >
                    <div className={'flex'} >
                        <input type={'search'} placeholder={'Indeks lub EAN'} {...searchForm.register('search')} />
                        <button className={'btn'} >{searchThrobber ? <i className="fas fa-sync fa-spin" /> : t('user-page.merchant-limit.search')}
                        </button>
                    </div>

                    {productsFind.length === 0 && startSearch ?  <div className={'empty'} >{t('user-page.merchant-limit.empty-product-list')}</div> :

                        <table>
                            {productsFind.map((product: ProductDataInterface, index: number) => {
                                return (
                                    <tr key={index} className={'product'} >
                                        <td><b>{product.name}</b></td>
                                        <td>{product.indexHurt}</td>
                                        <td>{product.ean}</td>
                                        <td><button className={'btn'} type={'button'} onClick={() => add(product)} >{t('user-page.merchant-limit.add')}</button></td>
                                    </tr>
                                );
                            })}
                        </table>

                    }
                </div>
            </form>
        </SimpleModal>
    )
}
